import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!url || !publishableKey) {
  // Loud failure at module-load so it surfaces in dev right away rather than
  // showing up as a confusing "Invalid API key" error mid-flow.
  // eslint-disable-next-line no-console
  console.error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY at build time.');
}

/**
 * No-op lock. supabase-js's default auth lock uses the navigator.locks API,
 * which we've observed deadlocking when sign-in is attempted immediately after
 * sign-out (the lock acquired during signOut isn't always released before the
 * next signInWithPassword tries to acquire it, leaving the promise hanging
 * forever). This SPA only ever runs one auth operation at a time per page, so
 * locking buys us nothing here.
 */
const noopLock = async <R>(_name: string, _acquireTimeoutMs: number, fn: () => Promise<R>): Promise<R> => fn();

/**
 * Browser-side Supabase client. The publishable key is safe to ship — RLS
 * is the data-level guardrail. autoRefreshToken is OFF: our own code
 * (./session.ts → getValidAccessToken) is the sole writer to the storage
 * key and refreshes on-demand before each authed request. Two refreshers
 * racing the same refresh_token would burn it (Supabase rotates refresh
 * tokens). persistSession stays on so detectSessionInUrl can write the
 * session from password-recovery / email-confirmation fragments.
 */
export const supabase: SupabaseClient = createClient(url, publishableKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: false,
    detectSessionInUrl: true,
    lock: noopLock,
  },
});
